import { useState, useEffect } from "react";
import { X, Image as ImageIcon, Loader2, CheckCircle, User } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { Mascota, obtenerMascotaDetalle, reportarMascotaEncontrada, ObtenerMascotaDetalleRequest } from "../../../../api/mascotaService";
import { obtenerTiposMascota, TipoMascota } from "../../../../api/tipoMascotaService";

interface LostPetDetailModalProps {
  isOpen: boolean;
  mascota: Mascota;
  onClose: (success?: boolean) => void;
}

export default function LostPetDetailModal({ isOpen, mascota, onClose }: LostPetDetailModalProps) {
  const { session } = useAuth();
  const unidadId = session?.unidad?.id || "";
  const apartamentoCodigo = session?.apartamento?.id || "";
  const tipoUsuarioCodigo = session?.rol?.id || "";
  
  const [mascotaDetalle, setMascotaDetalle] = useState<Mascota | null>(null);
  const [tiposMascota, setTiposMascota] = useState<TipoMascota[]>([]);
  const [isLoadingDetalle, setIsLoadingDetalle] = useState(false);
  const [isLoadingTipos, setIsLoadingTipos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar detalle y tipos al abrir el modal
  useEffect(() => {
    if (isOpen && mascota?.mas_codigo && unidadId) {
      loadDetalle();
      loadTiposMascota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mascota?.mas_codigo, unidadId]);

  const loadDetalle = async () => {
    if (!mascota?.mas_codigo || !unidadId) return;
    
    setIsLoadingDetalle(true);
    try {
      const payload: ObtenerMascotaDetalleRequest = {
        unidad_residencial_codigo: unidadId,
        torre_codigo: "",
        apartamento_codigo: "",
        mascota_codigo: String(mascota.mas_codigo),
        esta_perdida: "",
      };

      const response = await obtenerMascotaDetalle(payload);
      
      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        setMascotaDetalle(response.object_[0]);
      } else {
        // Si no se encuentra el detalle, usar la información inicial
        setMascotaDetalle(mascota);
      }
    } catch (error) {
      console.error("Error al cargar detalle:", error);
      // Si hay error, usar la información inicial
      setMascotaDetalle(mascota);
    } finally {
      setIsLoadingDetalle(false);
    }
  };

  const loadTiposMascota = async () => {
    setIsLoadingTipos(true);
    try {
      const response = await obtenerTiposMascota();
      if (response.code === "1" && response.object_) {
        setTiposMascota(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar tipos de mascota:", error);
    } finally {
      setIsLoadingTipos(false);
    }
  };

  const handleReportarEncontrada = async () => {
    if (!mascotaDetalle?.mas_codigo) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo obtener el código de la mascota.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    if (!apartamentoCodigo || !tipoUsuarioCodigo) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró la información del usuario para reportar.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        mascota_codigo: String(mascotaDetalle.mas_codigo),
        encontrado_por_apartamento_code: apartamentoCodigo,
        encontrado_por_tipo_usuario_code: tipoUsuarioCodigo,
      };

      const response = await reportarMascotaEncontrada(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.message || "La mascota ha sido reportada como encontrada exitosamente.",
          confirmButtonColor: "#1a3c34",
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al reportar la mascota");
      }
    } catch (error) {
      console.error("Error al reportar mascota:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "Error al reportar la mascota como encontrada.",
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getImageUrl = (rutaFoto?: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    const fotoString = String(rutaFoto).trim();
    if (!fotoString || fotoString === "") {
      return null;
    }
    if (fotoString.startsWith('http://') || fotoString.startsWith('https://')) {
      return fotoString;
    }
    if (fotoString.includes('img.easyph.com.co')) {
      return fotoString.startsWith('http') ? fotoString : `https://${fotoString}`;
    }
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${fotoString.replace(/^\//, '')}`;
  };

  if (!isOpen) return null;

  const mascotaMostrar = mascotaDetalle || mascota;
  const tipoMascotaSeleccionado = tiposMascota.find(
    (tipo) => 
      tipo.tma_codigo === mascotaMostrar.mas_tipo_mascota_codigo || 
      tipo.tma_codigo === (mascotaMostrar as any).mas_tipo
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg sm:text-xl font-bold text-military-600 flex items-center gap-2">
            <User className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="truncate">Detalle de Mascota Perdida</span>
          </h3>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-600 transition text-2xl font-bold leading-none"
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {isLoadingDetalle ? (
            <div className="flex flex-col items-center justify-center p-8 sm:p-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-500 mb-4" />
              <p className="text-gray-600 text-sm sm:text-base">Cargando información...</p>
            </div>
          ) : (
            <>
              {/* 1. Foto Mascota */}
              <div className="flex justify-center">
                {(() => {
                  const foto = mascotaMostrar.mas_foto_nombre || mascotaMostrar.mas_foto_ruta || (mascotaMostrar as any).mas_foto || (mascotaMostrar as any).mas_ruta_foto || (mascotaMostrar as any).foto;
                  const imageUrl = getImageUrl(foto);
                  
                  return (
                    <>
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={mascotaMostrar.mas_nombre || "Mascota"}
                          className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-xl object-cover border-4 border-military-500 shadow-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      )}
                      <div 
                        className={`w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-xl bg-gray-200 flex items-center justify-center border-4 border-military-500 shadow-lg ${imageUrl ? 'hidden' : ''}`}
                      >
                        <ImageIcon className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:h-24 text-gray-400" />
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {/* Información de la mascota */}
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {/* 2. Nombre */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2">Nombre</p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {mascotaMostrar.mas_nombre || "No especificado"}
                  </p>
                </div>
                
                {/* 3. Tipo de Mascota */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2">Tipo de Mascota</p>
                  {isLoadingTipos ? (
                    <Loader2 className="h-4 w-4 animate-spin text-military-500" />
                  ) : (
                    <p className="text-sm sm:text-base text-gray-900 break-words">
                      {tipoMascotaSeleccionado 
                        ? tipoMascotaSeleccionado.tma_nombre 
                        : mascotaMostrar.mas_tipo_mascota_nombre || (mascotaMostrar as any).mas_tipo_nombre || "No especificado"}
                    </p>
                  )}
                </div>
                
                {/* 4. Raza */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                  <p className="font-semibold text-xs sm:text-sm text-gray-700 mb-2">Raza</p>
                  <p className="text-sm sm:text-base text-gray-900 break-words">
                    {mascotaMostrar.mas_raza || "No especificada"}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={() => onClose()}
            variant="outline"
            className="w-full sm:w-auto text-sm sm:text-base"
            disabled={isSubmitting || isLoadingDetalle}
          >
            Cerrar
          </Button>
          {!isLoadingDetalle && (
            <Button
              onClick={handleReportarEncontrada}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white text-sm sm:text-base"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reportando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Reportar como Encontrada
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

